import {
  KitButton,
  KitSelect,
  KitText,
  KitTouchable,
} from '@omni/kit/components';
import KitDatePickerInput from '@omni/kit/components/KitDatePickerInput/KitDatePickerInput';
import { DateTimePickerEvent } from '@omni/kit/components/KitDatePickerInput/types';
import KitOptionSelector from '@omni/kit/components/KitOptionSelector';
import {
  WindowSizeMin,
  useSizeClass,
} from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import moment from 'moment';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';

import { useChannelGroup } from '../../../hooks/useChannelGroup';
import { parseRRule } from '../../../utilities/parseRrule';
import { generateRRule } from '../../../utilities/repeatEventRules';
import { accessTokenSelector, channelSelector } from '../../redux/selectors';

interface Props {
  navigation: any;
  route: any;
  onCancel?: () => void;
  onSaveWeb?: (data: any) => void;
}
type RefType = {
  closePicker: () => void;
};

export default function RepeatGroupEvent({
  route,
  navigation,
  onCancel,
  onSaveWeb,
}: Props): ReactElement {
  const { t } = useTranslation();
  const accessToken = useSelector(accessTokenSelector);
  const { handleSetIsRepeatsEdited } = route.params;
  const { eventData, event } = route.params;
  const [frequencyValue, setFrequencyValue] = useState(
    eventData?.frequency ?? 'never'
  );
  const isWeb = Platform.OS === 'web';
  const isIOS = Platform.OS === 'ios';
  const isAndroid = Platform.OS === 'android';
  const stopRepeatingDateRef = useRef<RefType>(null);
  const initialSelectedOption = eventData?.stopEventDate ? 1 : 0;
  const [selectedOption, setSelectedOption] = useState<number | null>(
    initialSelectedOption
  );
  const { startDate } = route.params;
  const [stopEventDate, setStopEventDate] = useState<Date>(
    eventData?.stopEventDate ?? startDate
  );
  const isEdited = useMemo(() => {
    return (
      frequencyValue !== eventData?.frequency ||
      selectedOption !== initialSelectedOption ||
      stopEventDate !== eventData?.stopEventDate
    );
  }, [
    frequencyValue,
    selectedOption,
    eventData,
    initialSelectedOption,
    stopEventDate,
  ]);

  // Move the state update logic to useEffect, so it's run after the render
  useEffect(() => {
    if (isEdited) {
      handleSetIsRepeatsEdited?.(true);
    }
  }, [isEdited, handleSetIsRepeatsEdited]);

  const { smallestWindowWidth, windowWidth, windowHeight } = useSizeClass();

  const isSmall =
    Platform.OS === 'web'
      ? windowWidth < WindowSizeMin.L
      : smallestWindowWidth < WindowSizeMin.L;

  // Get the week number of started date
  const getWeekNumberInMonth = (date: Date): number => {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfMonth = Math.floor(
      (date.getTime() - startOfMonth.getTime()) / 86400000 /* day in msec */
    );

    return Math.ceil((dayOfMonth + 1) / 7);
  };
  // Calculate the week number of start date
  const weekNumberInMonth = getWeekNumberInMonth(startDate);
  const isLandscape = windowHeight < windowWidth;
  const containerPadding = {
    borderRadius: 16,
    padding: isWeb || isLandscape ? Spacing.xl : 0,
    paddingHorizontal:
      (isIOS && !isLandscape) || isAndroid ? Spacing.l : Spacing.xl,
    paddingTop: (isIOS && !isLandscape) || isAndroid ? Spacing.m : Spacing.xl,
  };
  const options = [
    { id: '0', title: t('messaging:never') },
    { id: '1', title: t('messaging:onDate') },
  ];
  const channel = useSelector(channelSelector);
  const group = useChannelGroup(channel?.url);

  const groupTimezone = useMemo(() => group?.timezone ?? 'UTC', [group]);

  useEffect(() => {
    const parsedRule = parseRRule(event?.repetition_rules?.[1]);

    if (!eventData && parsedRule) {
      setFrequencyValue(parsedRule?.frequency);
      if (parsedRule.stopEventDate) {
        setSelectedOption(1);
        setStopEventDate(parsedRule.stopEventDate);
      } else {
        setSelectedOption(0);
      }
    } else if (eventData && parsedRule) {
      setFrequencyValue(eventData?.frequency);
      if (eventData.stopEventDate) {
        setSelectedOption(1);
        setStopEventDate(eventData.stopEventDate);
      } else {
        setSelectedOption(0);
      }
    }
  }, [event?.repetition_rules, eventData]);

  const onChangeStopEventDate = (
    _event?: DateTimePickerEvent,
    selectedDate?: Date
  ) => {
    const startDate = moment(selectedDate).startOf('day').toDate();
    setStopEventDate(startDate);
  };

  const handleSave = useCallback(() => {
    const rrule = generateRRule(
      frequencyValue,
      startDate,
      stopEventDate,
      selectedOption,
      groupTimezone,
      weekNumberInMonth,
      t
    );

    const dataToSend = {
      frequency: frequencyValue,
      stopEventDate: selectedOption === 1 ? stopEventDate : null,
      startDate: startDate,
      weekNumberInMonth: weekNumberInMonth,

      rrule: rrule, // Send RRULE to backend
    };

    if (isEdited) {
      if (Platform.OS !== 'web') {
        // On mobile platforms (iOS/Android), use navigation to pass data
        navigation.navigate('create_event', { eventData: dataToSend });
      } else {
        // On web, call the parent's onSave callback to handle saving and closing modal
        if (onSaveWeb) {
          onSaveWeb(dataToSend); // Pass the data to the parent
        }
      }
    }
  }, [
    t,
    isEdited,
    frequencyValue,
    stopEventDate,
    selectedOption,
    navigation,
    onSaveWeb,
    groupTimezone,
    startDate,
    weekNumberInMonth,
  ]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      navigation.setOptions({
        headerRight: () => (
          <KitTouchable
            onPress={() => handleSave()}
            disabled={!isEdited}
            style={{
              paddingLeft: 12,
              paddingRight: Platform.OS === 'ios' ? 12 : 18,
              paddingVertical: 6,
              marginRight: -6,
              borderRadius: 40,
            }}
          >
            <KitText
              color={isEdited ? Colors.brand : Colors.N400}
              fontSize={16}
            >
              {t('common:buttonTitleSave')}
            </KitText>
          </KitTouchable>
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdited, handleSave, navigation]);

  return (
    <TouchableWithoutFeedback
      onPress={() => {
        stopRepeatingDateRef?.current?.closePicker?.();
      }}
      accessible={false}
    >
      <View style={[styles.container, containerPadding]}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <KitText color={Colors.N900} style={styles.title}>
            {t('messaging:groupEventRepeatTitle')}
          </KitText>
        </View>
        <View style={styles.selectContainer}>
          <KitSelect
            items={[
              {
                value: 'never',
                label: t('messaging:never'),
              },
              {
                value: 'daily',
                label: t('groups:frequency_daily'),
              },
              {
                value: 'weekly',
                label: t(
                  'groups:frequency_every_interval_week_on_selected_day_one',
                  {
                    weekday: moment(startDate).format('dddd'),
                  }
                ),
              },
              {
                value: 'every_interval_week_on',
                label: t(
                  'groups:frequency_every_interval_week_on_selected_day_other',
                  {
                    count: 2,
                    weekday: moment(startDate).format('dddd'),
                  }
                ),
              },
              {
                value: 'monthly_on_selected_day',
                label: t('groups:frequency_monthly_on', {
                  weekNumberOrdinal: t('common:key', {
                    count: weekNumberInMonth,
                    ordinal: true,
                  }),
                  weekday: moment(startDate).format('dddd'),
                }),
              },
              {
                value: 'annually_on_month_day',
                label: t('groups:frequency_annually_on_month_day', {
                  month: moment(startDate).format('MMMM'),
                  dayOrdinal: t('common:key', {
                    count: startDate.getDate(),
                    ordinal: true,
                  }),
                }),
              },
            ]}
            label={t('messaging:frequencyLabel')}
            labelStyle={{ marginBottom: 8, color: Colors.N600 }}
            onValueChange={(value) => setFrequencyValue(value.value as string)}
            value={frequencyValue}
          />
        </View>
        {frequencyValue !== 'never' ? (
          <View style={styles.stopRepeatingContainer}>
            <KitText
              fontSize={14}
              color={Colors.N600}
              lineHeight={18}
              fontWeight={600}
            >
              {t('messaging:stopRepeatingTitle')}
            </KitText>
            <View style={styles.selectButtonsContainer}>
              <KitOptionSelector
                options={options}
                selectedOption={selectedOption}
                onSelectOption={setSelectedOption}
                row
                optionHeight={58}
                fontSize={16}
                dynamicBoldStyle
              />
              {selectedOption === 1 ? (
                <View style={styles.dateContainer}>
                  {Platform.OS === 'web' ? (
                    <KitText
                      lineHeight={18}
                      fontSize={14}
                      fontWeight={600}
                      color={Colors.N600}
                      style={{ marginBottom: 8 }}
                    >
                      {t('messaging:date')}
                    </KitText>
                  ) : null}
                  <KitDatePickerInput
                    ref={stopRepeatingDateRef}
                    date={stopEventDate}
                    onChange={onChangeStopEventDate}
                    minimumDate={startDate}
                    label={t('messaging:date')}
                  />
                </View>
              ) : null}
            </View>
          </View>
        ) : null}

        {(Platform.OS === 'web' && !isSmall) || isLandscape ? (
          <View style={[styles.buttonContainer]}>
            <KitButton
              secondary
              title={t('messaging:buttonTitleCancel')}
              onPress={isWeb ? onCancel : navigation.goBack}
              style={styles.rightButton}
            />
            <KitButton
              testID='save_button'
              title={t('common:buttonTitleSave')}
              color={Colors.N900}
              disabled={!isEdited}
              onPress={handleSave}
              style={styles.rightButton}
            />
          </View>
        ) : null}
      </View>
    </TouchableWithoutFeedback>
  );
}
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.N0,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: Platform.OS === 'web' ? 24 : 18,
  },
  selectContainer: {
    marginTop: Platform.OS !== 'android' ? -5 : 0,
    marginBottom: Spacing.l,
    // @ts-ignore
    appearance: 'none',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as 'solid' | 'dotted' | 'dashed' | undefined,
    }),
    width: '100%',
  },
  stopRepeatingContainer: {
    marginBottom: Spacing.l,
    // @ts-ignore
    appearance: 'none',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as 'solid' | 'dotted' | 'dashed' | undefined,
    }),
    width: '100%',
  },
  selectButtonsContainer: {
    marginTop: Spacing.s,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  dateContainer: {
    marginTop: Spacing.l,
  },
  rightButton: {
    marginLeft: Spacing.s,
    width: 100,
  },
});
